/**
 * AnalyticsHealthMonitor - Track analytics system health metrics
 * 
 * Monitors the health and performance of the analytics system by tracking
 * events sent, failed, queued, and initialization status.
 */

import { AnalyticsHealthMetrics } from './types';

export class AnalyticsHealthMonitor {
  private metrics: AnalyticsHealthMetrics;

  constructor() {
    this.metrics = {
      eventsQueued: 0,
      eventsSent: 0,
      eventsFailed: 0,
      lastSuccessfulSend: 0,
      initializationStatus: 'pending',
      optOutStatus: false,
    };
  }

  /**
   * Get current health status
   */
  getHealthStatus(): AnalyticsHealthMetrics {
    return { ...this.metrics };
  }

  /**
   * Record a successful event send
   */
  recordEventSent(): void {
    this.metrics.eventsSent++;
    this.metrics.lastSuccessfulSend = Date.now();
  }

  /**
   * Record a failed event send
   */
  recordEventFailed(): void {
    this.metrics.eventsFailed++;
  }

  /**
   * Record an event added to queue
   */
  recordEventQueued(): void {
    this.metrics.eventsQueued++;
  }

  /**
   * Record an event removed from queue (sent or discarded)
   */
  recordEventDequeued(): void {
    if (this.metrics.eventsQueued > 0) {
      this.metrics.eventsQueued--;
    }
  }

  /**
   * Set initialization status
   */
  setInitializationStatus(status: 'pending' | 'success' | 'failed'): void {
    this.metrics.initializationStatus = status;
  }

  /**
   * Set opt-out status
   */
  setOptOutStatus(optedOut: boolean): void {
    this.metrics.optOutStatus = optedOut;
  }

  /**
   * Get success rate (percentage of events sent successfully)
   */
  getSuccessRate(): number {
    const totalAttempts = this.metrics.eventsSent + this.metrics.eventsFailed;
    if (totalAttempts === 0) {
      return 100; // No failures if no attempts
    }
    return (this.metrics.eventsSent / totalAttempts) * 100;
  }

  /**
   * Check if system is healthy
   * Healthy if: initialized successfully, success rate > 95%, queue not full
   */
  isHealthy(): boolean {
    return (
      this.metrics.initializationStatus === 'success' &&
      this.getSuccessRate() >= 95 &&
      this.metrics.eventsQueued < 90 // Less than 90% of max queue size (100)
    );
  }

  /**
   * Get time since last successful send (in milliseconds)
   */
  getTimeSinceLastSend(): number {
    if (this.metrics.lastSuccessfulSend === 0) {
      return -1; // Never sent
    }
    return Date.now() - this.metrics.lastSuccessfulSend;
  }

  /**
   * Reset all metrics (useful for testing)
   */
  reset(): void {
    this.metrics = {
      eventsQueued: 0,
      eventsSent: 0,
      eventsFailed: 0,
      lastSuccessfulSend: 0,
      initializationStatus: 'pending',
      optOutStatus: false,
    };
  }

  /**
   * Get a summary of health metrics as a formatted string
   */
  getSummary(): string {
    const successRate = this.getSuccessRate().toFixed(2);
    const timeSinceLastSend = this.getTimeSinceLastSend();
    const lastSendStr = timeSinceLastSend === -1 
      ? 'Never' 
      : `${Math.round(timeSinceLastSend / 1000)}s ago`;

    return `
Analytics Health Summary:
- Initialization: ${this.metrics.initializationStatus}
- Events Sent: ${this.metrics.eventsSent}
- Events Failed: ${this.metrics.eventsFailed}
- Events Queued: ${this.metrics.eventsQueued}
- Success Rate: ${successRate}%
- Last Send: ${lastSendStr}
- Opt-Out: ${this.metrics.optOutStatus ? 'Yes' : 'No'}
- Status: ${this.isHealthy() ? '✓ Healthy' : '✗ Unhealthy'}
    `.trim();
  }
}
